/**
 * Sendbird client wrapper for messaging. Keeps Sendbird details out of components.
 */
import { GroupChannelHandler } from '@sendbird/chat/groupChannel';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import type { BaseMessage } from '@sendbird/chat/message';
import { MessageMetaArray } from '@sendbird/chat/message';
import { setupApollo } from '@/lib/apollo_setup';
import { readMeta } from '@/lib/page';
import { readDomainCookie, writeDomainCookie } from '@/lib/domain_cookie';
import { refreshedJwt } from '@/lib/jwt';
import { SENDBIRD_TOKEN_QUERY } from '@/graphql/messages/queries';
import { createMessagesClient } from '@/lib/messages/client';
import type { MessagesClient, MessagesClientConfig } from '@/lib/messages/client';
import channelCacheManager from './channelCacheManager';

interface SendbirdSessionToken {
  token: string;
  expiresAt: string;
}

/* eslint-disable no-unused-vars */
interface ConnectMessagingClientArgs {
  accountId: string;
  onMessageReceived?: (channel: GroupChannel, message: BaseMessage) => void;
}
/* eslint-enable no-unused-vars */

const apolloProvider = setupApollo();
const handlerId = 'messages-group-channel-handler';

let client: MessagesClient | null = null;
let clientConfig: MessagesClientConfig | null = null;

const getSessionTokenCookieName = (accountId: string): string => `user_${accountId}_sendbird_token`;

const isTokenExpiring = (expiresAt: string): boolean => {
  const expiryDate = new Date(expiresAt);
  if (Number.isNaN(expiryDate.getTime())) return true;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return expiryDate < tomorrow;
};

const getCachedSessionToken = (accountId: string): SendbirdSessionToken | null => {
  const cachedToken = readDomainCookie(getSessionTokenCookieName(accountId));
  if (!cachedToken) return null;

  try {
    return JSON.parse(cachedToken) as SendbirdSessionToken;
  } catch (_error) {
    return null;
  }
};

const fetchSessionToken = async (accountId: string): Promise<SendbirdSessionToken> => {
  const jwt = await refreshedJwt();
  if (!jwt) {
    throw new Error('Cannot access messages without being logged in.');
  }

  const res = await apolloProvider.defaultClient.query({
    query: SENDBIRD_TOKEN_QUERY,
    fetchPolicy: 'network-only',
  });

  const sessionToken = res.data?.sendbirdSessionToken;

  if (!sessionToken?.token || !sessionToken?.expiresAt) {
    throw new Error('Sendbird session token is missing.');
  }

  writeDomainCookie(getSessionTokenCookieName(accountId), JSON.stringify(sessionToken));

  return sessionToken;
};

const getSendbirdToken = async (accountId: string): Promise<string> => {
  const cachedToken = getCachedSessionToken(accountId);
  if (cachedToken && !isTokenExpiring(cachedToken.expiresAt)) {
    return cachedToken.token;
  }

  const sessionToken = await fetchSessionToken(accountId);
  return sessionToken.token;
};

export const getSendbirdApplicationId = (): string | null => readMeta('sendbird_application_id');

export const connectMessagingClient = async ({ accountId, onMessageReceived }: ConnectMessagingClientArgs): Promise<void> => {
  const applicationId = getSendbirdApplicationId();
  if (!applicationId) {
    throw new Error('Sendbird application id is missing.');
  }

  const accessToken = await getSendbirdToken(accountId);
  const nextConfig: MessagesClientConfig = {
    applicationId,
    userId: accountId,
    accessToken,
  };

  const needsNewClient =
    !client ||
    !clientConfig ||
    clientConfig.applicationId !== applicationId ||
    clientConfig.userId !== accountId ||
    clientConfig.accessToken !== accessToken;

  if (needsNewClient) {
    client?.disconnect();
    client = createMessagesClient(nextConfig);
    clientConfig = nextConfig;
  }

  if (!client) {
    throw new Error('Messaging client is not initialized.');
  }

  await client.connect();

  if (onMessageReceived) {
    client.removeGroupChannelHandler(handlerId);
    const handler = new GroupChannelHandler({
      onMessageReceived: (channel, message) => onMessageReceived(channel as GroupChannel, message as BaseMessage),
    });
    client.addGroupChannelHandler(handlerId, handler);
  }
};

const getConnectedSdk = (): ReturnType<MessagesClient['getSdk']> => {
  if (!client) {
    throw new Error('Messaging client is not initialized.');
  }

  return client.getSdk();
};

export const fetchGroupChannels = async (limit = 100): Promise<GroupChannel[]> => {
  const sdk = getConnectedSdk() as any;
  const query = sdk.groupChannel.createMyGroupChannelListQuery({ limit });
  const channels = await query.next();
  return channels || [];
};

export const fetchChannelByUrl = async (channelUrl: string): Promise<GroupChannel> => {
  const sdk = getConnectedSdk() as any;
  return sdk.groupChannel.getChannel(channelUrl);
};

export const fetchChannelWithMessages = async (
  channelUrl: string,
  limit = 50
): Promise<{ channel: GroupChannel; messages: BaseMessage[] }> => {
  // Use cached channel if available to reduce network calls and UI blinks
  const channel = await channelCacheManager.getOrFetchChannel(channelUrl);
  await channel.markAsRead();
  const query = channel.createPreviousMessageListQuery({
    limit,
    reverse: false,
    includeMetaArray: true,
  });
  const messages = await query.load();
  return { channel, messages: messages || [] };
};

export const sendChannelMessage = async (channelUrl: string, message: string): Promise<BaseMessage> => {
  const channel = await fetchChannelByUrl(channelUrl);
  const sessionId = readMeta('session');
  const params = {
    message,
    metaArrays: sessionId ? [new MessageMetaArray({ key: 'session', value: [sessionId] })] : [],
  };

  const handler = channel.sendUserMessage(params);
  return new Promise<BaseMessage>((resolve, reject) => {
    handler.onSucceeded((msg) => resolve(msg as BaseMessage));
    handler.onFailed((err) => reject(err ?? new Error('Failed to send message')));
  });
};

export const disconnectMessagingClient = (): void => {
  if (!client) return;

  client.removeGroupChannelHandler(handlerId);
  client.disconnect();
  client = null;
  clientConfig = null;
  channelCacheManager.clear();
};
